import React, { FC, useEffect, useState } from "react"

import {
    ApiOutlined,
    CheckCircleFilled,
    EnvironmentOutlined,
    HomeOutlined,
    LoadingOutlined,
    MinusCircleFilled,
    PhoneFilled,
    SoundOutlined
} from "@ant-design/icons"
import { Avatar, Badge, Button, Checkbox, Dropdown, Layout, Modal, Space, Spin, Tooltip } from "antd"

import styles from "./Header.module.scss"
import { useActions } from "../../hooks/useActions"
import { useTypedSelector } from "../../hooks/useTypedSelector"
import { updateArrayItem } from "../../utils/data"
import { isSoundEnabled, setSoundEnabled, playTestSound } from "../../utils/sound"
import { showErrorNotification, showSuccessNotification } from "../../utils/ui"
import { mainApi } from "../../api/endpoints/mainApi"
import { useAllowedRoles } from "../../hooks/useAllowedRoles"
import { adminRole } from "../../constants/roles"

const Header: FC = () => {
    const isAdmin = useAllowedRoles([adminRole])
    const [modalOpen, setModalOpen] = useState(false)
    const [checkedList, setCheckedList] = useState<{ id: number, status: boolean, name: string }[]>([])
    const [soundOn, setSoundOn] = useState(isSoundEnabled())

    const { name, user_locations_list, user_phone_status, user_app_status, cian_auth_status, cian_subscription, locationsLoading } = useTypedSelector((state) => state.currentUser)
    const { currentUserGetLocations, currentUserUpdatePhoneStatus, currentUserUpdateLocations } = useActions()
    const [photoBalance, setPhotoBalance] = useState({
        date: "",
        "photo-balance": 0,
        isLoading: true
    })

    useEffect(() => {
        const fetchPhotoBalance = async () => {
            setPhotoBalance((prev) => ({ ...prev, isLoading: true }))
            try {
                const { data } = await mainApi.getUserPhotoBalance()
                setPhotoBalance(({ ...data, isLoading: false }))
            } catch (err) {
                showErrorNotification("Произошла ошибка при получении баланса фотографий.")
                setPhotoBalance((prev) => ({ ...prev, isLoading: false }))
                console.log(err)
            }
        }
        isAdmin && fetchPhotoBalance()
        if (modalOpen) {
            currentUserGetLocations()
        }
    }, [modalOpen])

    useEffect(() => {
        setCheckedList(user_locations_list)
    }, [user_locations_list])

    const userStatusMenuItems = [
        {
            key: "status-busy",
            label: (
                <div className={styles.dropdownItem} onClick={() => currentUserUpdatePhoneStatus(4)}>
                    <MinusCircleFilled style={{ color: "#ef4444", fontSize: 16 }} />
                    <span>Не беспокоить</span>
                </div>
            )
        },
        {
            key: "status-ready",
            label: (
                <div className={styles.dropdownItem} onClick={() => currentUserUpdatePhoneStatus(2)}>
                    <CheckCircleFilled style={{ color: "#22c55e", fontSize: 16 }} />
                    <span>Готов</span>
                </div>
            )
        },
    ]

    // Генерируем инициалы из имени
    const getInitials = (fullName: string) => {
        if (!fullName) return "U"
        const parts = fullName.split(" ")
        if (parts.length >= 2) {
            return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
        }
        return fullName[0].toUpperCase()
    }

    return (
        <Layout.Header className={styles.headerContainer}>
            <div />

            <div className={styles.headerRight}>
                {/* Баланс фото */}
                {isAdmin && (
                    <Spin spinning={photoBalance.isLoading} indicator={<LoadingOutlined style={{ fontSize: 16 }} spin />}>
                        <div className={styles.balanceInfo}>
                            <div className={styles.balanceRow}>
                                <span className={styles.balanceLabel}>Фото:</span>
                                <span className={styles.balanceValue}>{photoBalance["photo-balance"]}</span>
                            </div>
                            <div className={styles.balanceRow}>
                                <span className={styles.balanceLabel}>До:</span>
                                <span className={styles.balanceDate}>{photoBalance.date}</span>
                            </div>
                        </div>
                    </Spin>
                )}

                {/* Разделитель */}
                <div className={styles.divider} />

                {/* Профиль пользователя */}
                <div className={styles.userProfile}>
                    <Avatar 
                        size={36} 
                        className={styles.avatar}
                        style={{ 
                            backgroundColor: '#3b82f6',
                            fontSize: 14,
                            fontWeight: 600
                        }}
                    >
                        {getInitials(name)}
                    </Avatar>
                    <span className={styles.userName}>
                        {name || "Пользователь"}
                    </span>
                </div>

                {/* Разделитель */}
                <div className={styles.divider} />

                {/* Кнопки действий */}
                <Space size={4}>
                    <Tooltip title={soundOn ? "Звук уведомлений включён" : "Звук уведомлений выключен"}>
                        <Button
                            type="text"
                            className={styles.iconButton}
                            style={{ opacity: soundOn ? 1 : 0.4 }}
                            icon={<SoundOutlined />}
                            onClick={() => {
                                const newValue = !soundOn
                                setSoundOn(newValue)
                                setSoundEnabled(newValue)
                                if (newValue) {
                                    playTestSound()
                                }
                            }}
                        />
                    </Tooltip>

                    <Tooltip title="Выбор локаций">
                        <Button
                            type="text"
                            className={styles.iconButton}
                            icon={<EnvironmentOutlined />}
                            onClick={() => setModalOpen(true)}
                        />
                    </Tooltip>

                    <Tooltip title={cian_auth_status === 1 ? "ЦИАН авторизован" : "ЦИАН не авторизован"}>
                        <Badge 
                            count={cian_subscription?.limit_info?.match(/\d+/)?.[0] || null}
                            offset={[-2, 0]}
                            style={{ 
                                backgroundColor: cian_auth_status === 1 ? "#22c55e" : "#ef4444",
                                fontSize: 10,
                                minWidth: 16,
                                height: 16,
                                lineHeight: "16px",
                                borderRadius: 8,
                                boxShadow: "0 0 0 1px #fff"
                            }}
                        >
                            <Button
                                type="text"
                                className={styles.iconButton}
                                icon={<HomeOutlined />}
                            />
                        </Badge>
                    </Tooltip>

                    <Tooltip title={user_app_status === 2 ? "WebSocket подключен" : "WebSocket отключен"}>
                        <Badge 
                            dot 
                            offset={[-4, 4]}
                            style={{ 
                                backgroundColor: user_app_status === 2 ? "#22c55e" : "#ef4444",
                                boxShadow: `0 0 0 2px #fff`
                            }}
                        >
                            <Button
                                type="text"
                                className={styles.iconButton}
                                icon={<ApiOutlined />}
                            />
                        </Badge>
                    </Tooltip>

                    <Dropdown
                        menu={{ items: userStatusMenuItems }}
                        placement="bottomRight"
                        trigger={["click"]}
                        overlayClassName={styles.statusDropdown}
                    >
                        <Tooltip title={user_phone_status === 2 ? "Готов" : "Не беспокоить"}>
                            <Badge 
                                dot 
                                offset={[-4, 4]}
                                style={{ 
                                    backgroundColor: user_phone_status === 2 ? "#22c55e" : "#ef4444",
                                    boxShadow: `0 0 0 2px #fff`
                                }}
                            >
                                <Button
                                    type="text"
                                    className={styles.iconButton}
                                    icon={<PhoneFilled />}
                                />
                            </Badge>
                        </Tooltip>
                    </Dropdown>
                </Space>
            </div>

            {/* Модальное окно выбора локаций */}
            <Modal
                title="Выбор локаций"
                open={modalOpen}
                onCancel={() => setModalOpen(false)}
                onOk={() => {
                    currentUserUpdateLocations(checkedList, () => {
                        showSuccessNotification("Выбранные локации обновлены")
                        setModalOpen(false)
                    }, () => null)
                }}
                okText="Сохранить"
                cancelText="Отмена"
                okButtonProps={{ loading: locationsLoading }}
                className={styles.locationsModal}
                width={400}
            >
                <div className={styles.locationsContent}>
                    <div className={styles.locationsActions}>
                        <Button
                            size="small"
                            onClick={() => {
                                setCheckedList(
                                    checkedList.map((item) => ({
                                        ...item,
                                        status: true
                                    }))
                                )
                            }}
                        >
                            Выбрать все
                        </Button>

                        <Button
                            size="small"
                            onClick={() => {
                                setCheckedList(
                                    checkedList.map((item) => ({
                                        ...item,
                                        status: false
                                    }))
                                )
                            }}
                        >
                            Очистить все
                        </Button>
                    </div>

                    <div className={styles.locationsList}>
                        {checkedList.map((item) => (
                            <div 
                                key={item.id} 
                                className={styles.locationItem}
                                onClick={() => {
                                    let newList = updateArrayItem(item.id, { status: !item.status }, checkedList)
                                    setCheckedList(newList)
                                }}
                            >
                                <Checkbox checked={item.status}>
                                    {item.name}
                                </Checkbox>
                            </div>
                        ))}
                    </div>
                </div>
            </Modal>
        </Layout.Header>
    )
}

export default Header
